import { Trans, useLingui } from '@lingui/react/macro'
import {
  Badge,
  Callout,
  Hint,
  Panel,
  PanelTitle,
  Row,
  StatTable,
} from 'features/sentinelDesign/designStyles'
import { EvaluationPanel } from 'features/sentinelDesign/EvaluationPanel'
import { isRenderablePlan } from 'features/sentinelDesign/planEdits'
import { PlanSummary } from 'features/sentinelDesign/PlanSummary'
import { RunResultsPanel } from 'features/sentinelDesign/RunResultsPanel'
import type { SentinelPlanWarning } from 'features/sentinelDesign/types'
import { useDurableDesignArtifacts } from 'features/sentinelDesign/useDurableDesign'
import { RecentScorePanel } from 'features/sentinels/components/RecentScorePanel'
import type {
  SentinelDesignRunResultFile,
  SentinelRecordIdentity,
} from 'features/sentinels/recordTypes'
import type { SentinelEntryData } from 'features/sentinels/types'
import { FC } from 'react'

const severityTone = (severity: string | undefined) =>
  severity === 'error' ? 'error' : 'warning'

/** Every design warning, whether or not it named a plan field. */
const DesignWarnings: FC<{ warnings: SentinelPlanWarning[] }> = ({
  warnings,
}) => {
  const { t } = useLingui()
  return (
    <Panel aria-label={t`Design warnings`}>
      <Row>
        <PanelTitle>
          <Trans>Design warnings</Trans>
        </PanelTitle>
        <Badge $tone='warning'>{warnings.length}</Badge>
      </Row>
      <Hint>
        <Trans>
          Raised when the plan was designed and validated against the fit.
          Advisory only — none of them blocked this run.
        </Trans>
      </Hint>
      {warnings.map((warning, index) => (
        <Callout
          key={`${warning.code ?? ''}-${warning.field ?? ''}-${index}`}
          $tone={severityTone(warning.severity)}>
          {warning.code ? <strong>{warning.code}: </strong> : null}
          {warning.message}
          {warning.field ? ` (${warning.field})` : ''}
        </Callout>
      ))}
    </Panel>
  )
}

export interface DesignRunOverviewSectionsProps {
  record: SentinelDesignRunResultFile & SentinelRecordIdentity
  entryId?: string
  /** The run entry's file pointers, for the artifacts stored beside the fit. */
  files?: SentinelEntryData['files']
  /** Fired when the Live check opens a case, so hosts can reveal it. */
  onCaseOpened?: () => void
}

/**
 * The model side of a finished agentic-design run: plan, fitted results,
 * design warnings, run provenance, evaluation, and the live check. Shared by
 * the two-tab `DesignRunDetails` layout and the sentinel page's Model tab.
 */
export const DesignRunOverviewSections: FC<DesignRunOverviewSectionsProps> = ({
  record,
  entryId,
  files,
  onCaseOpened,
}) => {
  const { t } = useLingui()

  const { evaluation } = useDurableDesignArtifacts(
    entryId,
    files?.evaluation ? { evaluation: files.evaluation } : undefined
  )
  const warnings = record.plan_warnings ?? []
  const methods = record.methods ?? {}
  const methodLabels = Object.fromEntries(
    Object.entries(methods).map(([key, method]) => [key, method.label || key])
  )

  return (
    <>
      {record.refit && !record.refit.ok && (
        <Callout $tone='error'>
          <Trans>
            The full-window refit failed, so this sentinel has no served model
            and cannot score. The metrics below are still the honest split fit.
          </Trans>
        </Callout>
      )}

      {isRenderablePlan(record.plan) ? (
        <PlanSummary
          plan={record.plan}
          rationale={record.plan_rationale}
          narrative={record.plan_narrative}
          frozenBecause={t`This plan has been fitted, so it is history rather than a draft.`}
        />
      ) : (
        <Callout $tone='warning'>
          <Trans>
            This run stored no readable plan, so what was fitted cannot be
            shown.
          </Trans>
        </Callout>
      )}

      {Object.keys(methods).length > 0 ? (
        <RunResultsPanel
          run={{ ...record, methods, target: record.target ?? '' }}
        />
      ) : (
        <Callout $tone='error'>
          <Trans>
            This run stored no fitted methods, so there are no results to show.
          </Trans>
        </Callout>
      )}

      {warnings.length > 0 && <DesignWarnings warnings={warnings} />}

      <Panel aria-label={t`Run provenance`}>
        <Row>
          <PanelTitle>
            <Trans>How this run was produced</Trans>
          </PanelTitle>
          {record.schema_version !== undefined && (
            <Badge>
              <Trans>schema v</Trans>
              {record.schema_version}
            </Badge>
          )}
        </Row>
        <StatTable>
          <tbody>
            <tr>
              <th scope='row'>
                <Trans>Window</Trans>
              </th>
              <td>
                {record.window?.start ?? '—'} → {record.window?.end ?? '—'}
              </td>
            </tr>
            <tr>
              <th scope='row'>
                <Trans>Served model</Trans>
              </th>
              <td>
                {record.refit
                  ? `${record.refit.ok ? t`refit on the` : t`refit FAILED — `}${record.refit.policy}`
                  : t`the split fit above`}
              </td>
            </tr>
            <tr>
              <th scope='row'>
                <Trans>Design context</Trans>
              </th>
              <td>{record.context?.entry_id || '—'}</td>
            </tr>
            <tr>
              <th scope='row'>
                <Trans>Run id</Trans>
              </th>
              <td>{record.run_id || '—'}</td>
            </tr>
          </tbody>
        </StatTable>
      </Panel>

      {evaluation && <EvaluationPanel evaluation={evaluation} />}

      {entryId && (
        <Panel aria-label={t`Live check`}>
          <PanelTitle>
            <Trans>
              Live check — score a recent window with the saved models
            </Trans>
          </PanelTitle>
          <Hint>
            <Trans>
              Pulls fresh data and scores it with the refit model. No
              retraining, and nothing is saved.
            </Trans>
          </Hint>
          <RecentScorePanel
            entryId={entryId}
            site={record.site_id}
            famOf={{}}
            mode='target'
            target={record.target}
            methodLabels={methodLabels}
            onCaseOpened={onCaseOpened}
          />
        </Panel>
      )}
    </>
  )
}
